// M10 不等式與線性規劃 - 主題 21-26 數據文件
// Inequalities and Linear Programming - Topics 21-26
// 系統化設計，準備後續 Supabase 數據遷移

// M10-21: 圖解法 (Graphical Method)
export const M10_TOPICS_21_TO_26 = [
  {
    id: 'M10_21',
    name: '圖解法',
    description: '用圖解法解決線性規劃問題',
    category: 'graphical_method',
    difficulty: 'basic'
  },
  {
    id: 'M10_22',
    name: '數線分析',
    description: '在數線上分析不等式的解',
    category: 'number_line_analysis',
    difficulty: 'basic'
  },
  {
    id: 'M10_23',
    name: '線性規劃求解',
    description: '線性規劃的標準求解方法',
    category: 'linear_programming_solution',
    difficulty: 'intermediate'
  },
  {
    id: 'M10_24',
    name: '複合不等式',
    description: '多個不等式組成的複合不等式',
    category: 'compound_inequalities',
    difficulty: 'intermediate'
  },
  {
    id: 'M10_25',
    name: '整數規劃',
    description: '變量限制為整數的規劃問題',
    category: 'integer_programming',
    difficulty: 'advanced'
  },
  {
    id: 'M10_26',
    name: '臨界點分析',
    description: '分析函數的臨界點與極值',
    category: 'critical_point_analysis',
    difficulty: 'advanced'
  }
];

// M10 問題數據 - 主題 21-26
export const M10_PROBLEMS_21_TO_26 = [
  // M10-21: 圖解法
  {
    id: 'M10_21_01',
    topicId: 'M10_21',
    type: 'multi_part',
    difficulty: 'basic',
    question: '線性規劃的圖解法：',
    parts: {
      a: {
        question: '用圖解法求解：$\\max z = 2x + 3y$，約束條件：$x + y \\leq 6, 2x + y \\leq 8, x \\geq 0, y \\geq 0$',
        solution: {
          steps: [
            '畫出約束條件確定的可行域',
            '約束線：$x + y = 6$，$2x + y = 8$，$x = 0$，$y = 0$',
            '找出可行域的頂點：$(0,0)$，$(0,6)$，$(2,4)$，$(4,0)$',
            '計算各頂點的目標函數值：$z(0,0) = 0$，$z(0,6) = 18$，$z(2,4) = 16$，$z(4,0) = 8$',
            '最大值在 $(0,6)$ 處取得'
          ],
          answer: '最優解 $(0,6)$，最大值 $18$'
        },
        hint: '畫出可行域，檢查所有頂點'
      },
      b: {
        question: '分析目標函數等值線的方向，驗證上題結果',
        solution: {
          steps: [
            '目標函數 $z = 2x + 3y$ 的等值線方程為 $2x + 3y = c$',
            '等值線斜率為 $-\\frac{2}{3}$',
            '約束邊界斜率：$x + y = 6$ 的斜率為 $-1$，$2x + y = 8$ 的斜率為 $-2$',
            '由於 $-1 < -\\frac{2}{3} < -2$，等值線與 $x + y = 6$ 的夾角更小',
            '沿著等值線斜率方向移動，最後接觸的頂點是 $(0,6)$'
          ],
          answer: '等值線分析確認最優解為 $(0,6)$'
        },
        hint: '比較等值線斜率與約束邊界斜率'
      },
      c: {
        question: '如果約束條件增加 $x + 2y \\leq 10$，重新求解',
        solution: {
          steps: [
            '新的約束條件：$x + y \\leq 6$，$2x + y \\leq 8$，$x + 2y \\leq 10$，$x \\geq 0$，$y \\geq 0$',
            '畫出新的可行域，找出頂點',
            '新約束 $x + 2y = 10$ 與原約束的交點：',
            '與 $x + y = 6$ 交於 $(2,4)$，與 $2x + y = 8$ 交於 $(6,-2)$（不在第一象限）',
            '新的頂點：$(0,0)$，$(0,5)$，$(2,4)$，$(4,0)$',
            '計算目標函數值，選擇最大者'
          ],
          answer: '需要重新計算各頂點的函數值'
        },
        hint: '增加約束可能改變可行域的形狀'
      }
    }
  },
  {
    id: 'M10_21_02',
    topicId: 'M10_21',
    type: 'multi_part',
    difficulty: 'basic',
    question: '圖解法的幾何理解：',
    parts: {
      a: {
        question: '解釋為什麼線性規劃的最優解總是在頂點上',
        solution: {
          steps: [
            '線性規劃的目標函數是線性的，等值線是直線',
            '可行域是由線性約束確定的凸多邊形',
            '當等值線平移時，最後接觸可行域的點必定是頂點或邊界',
            '如果最優解在邊界上，則整條邊界都是最優解，端點（頂點）也是最優解',
            '因此，總可以在頂點中找到最優解'
          ],
          answer: '線性函數在凸集上的最值必在頂點取得'
        },
        hint: '利用線性函數和凸集的性質'
      },
      b: {
        question: '什麼情況下線性規劃問題無解或無界？',
        solution: {
          steps: [
            '無解情況：約束條件矛盾，可行域為空集',
            '例如：$x + y \\leq 1$ 和 $x + y \\geq 2$ 同時成立',
            '無界情況：可行域無界且目標函數在無界方向上可以任意大',
            '例如：$\\max z = x + y$，約束只有 $x \\geq 0, y \\geq 0$',
            '有界但無最優解：理論上可能，但線性規劃中不會出現'
          ],
          answer: '無解：可行域空；無界：目標函數可任意大'
        },
        hint: '分析可行域的性質和目標函數的行為'
      },
      c: {
        question: '如何處理最小化問題？',
        solution: {
          steps: [
            '最小化問題 $\\min z = cx$ 等價於最大化問題 $\\max (-z) = -cx$',
            '或者直接在圖解法中尋找使目標函數值最小的頂點',
            '等值線移動方向與最大化問題相反',
            '最優解仍然在頂點上'
          ],
          answer: '轉化為最大化問題或直接求最小值頂點'
        },
        hint: '利用對偶性或直接求解'
      }
    }
  },

  // M10-22: 數線分析
  {
    id: 'M10_22_01',
    topicId: 'M10_22',
    type: 'multi_part',
    difficulty: 'basic',
    question: '數線分析法：',
    parts: {
      a: {
        question: '用數線分析法解不等式 $\\frac{(x-1)(x+2)}{x-3} > 0$',
        solution: {
          steps: [
            '找出零點和極點：$x = 1, -2$ (零點)，$x = 3$ (極點)',
            '在數線上標出這些關鍵點：$-2, 1, 3$',
            '將數線分為四個區間：$(-\\infty, -2)$，$(-2, 1)$，$(1, 3)$，$(3, +\\infty)$',
            '在每個區間內測試符號：',
            '區間 $(-\\infty, -2)$：三個因子都為負，乘積為負',
            '區間 $(-2, 1)$：$(x+2) > 0$，其他為負，乘積為正',
            '區間 $(1, 3)$：$(x-1) > 0, (x+2) > 0, (x-3) < 0$，乘積為負',
            '區間 $(3, +\\infty)$：三個因子都為正，乘積為正'
          ],
          answer: '$x \\in (-2, 1) \\cup (3, +\\infty)$'
        },
        hint: '標出零點和極點，分區間判斷符號'
      },
      b: {
        question: '解不等式組 $\\begin{cases} x^2 - 5x + 6 < 0 \\\\ x^2 - 1 > 0 \\end{cases}$',
        solution: {
          steps: [
            '分別解每個不等式：',
            '$x^2 - 5x + 6 < 0$：$(x-2)(x-3) < 0$，解得 $x \\in (2, 3)$',
            '$x^2 - 1 > 0$：$(x-1)(x+1) > 0$，解得 $x \\in (-\\infty, -1) \\cup (1, +\\infty)$',
            '在數線上標出兩個解集',
            '求交集：$(2, 3) \\cap [(-\\infty, -1) \\cup (1, +\\infty)] = (2, 3)$'
          ],
          answer: '$x \\in (2, 3)$'
        },
        hint: '分別求解，然後求交集'
      },
      c: {
        question: '分析不等式 $|x-1| + |x+2| \\leq 5$ 的解',
        solution: {
          steps: [
            '關鍵點：$x = 1, x = -2$，分三個區間討論',
            '區間 $x \\leq -2$：$|x-1| = 1-x, |x+2| = -x-2$',
            '不等式為：$(1-x) + (-x-2) \\leq 5$，即 $-2x - 1 \\leq 5$，得 $x \\geq -3$',
            '結合 $x \\leq -2$，得 $x \\in [-3, -2]$',
            '區間 $-2 < x < 1$：$|x-1| = 1-x, |x+2| = x+2$',
            '不等式為：$(1-x) + (x+2) \\leq 5$，即 $3 \\leq 5$ 恆成立',
            '所以 $x \\in (-2, 1)$ 都是解',
            '區間 $x \\geq 1$：$|x-1| = x-1, |x+2| = x+2$',
            '不等式為：$(x-1) + (x+2) \\leq 5$，即 $2x + 1 \\leq 5$，得 $x \\leq 2$',
            '結合 $x \\geq 1$，得 $x \\in [1, 2]$'
          ],
          answer: '$x \\in [-3, 2]$'
        },
        hint: '絕對值不等式按零點分區間討論'
      }
    }
  },

  // M10-23: 線性規劃求解
  {
    id: 'M10_23_01',
    topicId: 'M10_23',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '線性規劃的標準求解：',
    parts: {
      a: {
        question: '將線性規劃問題化為標準形式：$\\min z = -x_1 + 2x_2$，約束：$x_1 + x_2 \\geq 3, 2x_1 - x_2 \\leq 4, x_1 \\geq 0, x_2$ 無約束',
        solution: {
          steps: [
            '目標函數最小化轉為最大化：$\\max (-z) = x_1 - 2x_2$',
            '不等式約束轉為等式：引入鬆弛變量和剩餘變量',
            '$x_1 + x_2 - s_1 = 3$ (剩餘變量 $s_1 \\geq 0$)',
            '$2x_1 - x_2 + s_2 = 4$ (鬆弛變量 $s_2 \\geq 0$)',
            '無約束變量 $x_2$ 分解：$x_2 = x_2^+ - x_2^-$，其中 $x_2^+, x_2^- \\geq 0$',
            '標準形式：$\\max z\' = x_1 - 2x_2^+ + 2x_2^-$',
            '約束：$x_1 + x_2^+ - x_2^- - s_1 = 3$，$2x_1 - x_2^+ + x_2^- + s_2 = 4$',
            '所有變量非負：$x_1, x_2^+, x_2^-, s_1, s_2 \\geq 0$'
          ],
          answer: '標準形式建立完成'
        },
        hint: '引入鬆弛/剩餘變量，分解無約束變量'
      },
      b: {
        question: '用單純形法求解標準線性規劃：$\\max z = 3x_1 + 2x_2$，約束：$x_1 + x_2 \\leq 4, 2x_1 + x_2 \\leq 6, x_1, x_2 \\geq 0$',
        solution: {
          steps: [
            '引入鬆弛變量：$x_1 + x_2 + s_1 = 4$，$2x_1 + x_2 + s_2 = 6$',
            '初始單純形表：基變量 $s_1, s_2$，非基變量 $x_1, x_2$',
            '檢驗數：$c_1 - z_1 = 3 > 0$，$c_2 - z_2 = 2 > 0$，選 $x_1$ 進基',
            '最小比值檢驗：$\\min\\{\\frac{4}{1}, \\frac{6}{2}\\} = 3$，$s_2$ 出基',
            '主元變換後，繼續迭代直到所有檢驗數非正'
          ],
          answer: '最優解 $(2, 2)$，最大值 $10$'
        },
        hint: '建立初始表，進行主元變換迭代'
      },
      c: {
        question: '分析對偶問題的經濟意義',
        solution: {
          steps: [
            '原問題：資源配置，最大化收益',
            '對偶問題：資源定價，最小化成本',
            '對偶變量表示資源的影子價格',
            '強對偶定理：原問題和對偶問題的最優值相等',
            '互補鬆弛條件：原問題的約束不緊時，對偶變量為零'
          ],
          answer: '對偶理論提供了資源定價的經濟解釋'
        },
        hint: '對偶變量是資源的邊際價值'
      }
    }
  },

  // M10-24: 複合不等式
  {
    id: 'M10_24_01',
    topicId: 'M10_24',
    type: 'multi_part',
    difficulty: 'intermediate',
    question: '複合不等式的求解：',
    parts: {
      a: {
        question: '解不等式組 $\\begin{cases} |x-1| < 3 \\\\ |x+2| > 1 \\end{cases}$',
        solution: {
          steps: [
            '分別解每個不等式：',
            '$|x-1| < 3$：$-3 < x-1 < 3$，即 $-2 < x < 4$',
            '$|x+2| > 1$：$x+2 > 1$ 或 $x+2 < -1$，即 $x > -1$ 或 $x < -3$',
            '第二個不等式的解集：$x \\in (-\\infty, -3) \\cup (-1, +\\infty)$',
            '求交集：$(-2, 4) \\cap [(-\\infty, -3) \\cup (-1, +\\infty)]$',
            '$= (-2, 4) \\cap (-1, +\\infty) = (-1, 4)$'
          ],
          answer: '$x \\in (-1, 4)$'
        },
        hint: '分別求解，然後求交集'
      },
      b: {
        question: '解不等式 $\\frac{x^2-1}{x^2-4} \\geq 0$ 且 $x^2 - 3x + 2 < 0$',
        solution: {
          steps: [
            '第一個不等式：$\\frac{(x-1)(x+1)}{(x-2)(x+2)} \\geq 0$',
            '零點：$x = \\pm 1$，極點：$x = \\pm 2$',
            '數線分析：解集為 $x \\in [-1, 1] \\cup (2, +\\infty) \\cup (-\\infty, -2)$',
            '第二個不等式：$x^2 - 3x + 2 = (x-1)(x-2) < 0$',
            '解得：$x \\in (1, 2)$',
            '求交集：$[[-1, 1] \\cup (2, +\\infty) \\cup (-\\infty, -2)] \\cap (1, 2) = \\emptyset$'
          ],
          answer: '無解'
        },
        hint: '注意定義域的限制'
      },
      c: {
        question: '求滿足 $\\sqrt{x-1} + \\sqrt{4-x} \\leq 2$ 的 $x$ 值範圍',
        solution: {
          steps: [
            '定義域：$x-1 \\geq 0$ 且 $4-x \\geq 0$，即 $1 \\leq x \\leq 4$',
            '設 $u = \\sqrt{x-1}, v = \\sqrt{4-x}$，則 $u^2 + v^2 = 3$',
            '約束：$u + v \\leq 2$，$u, v \\geq 0$',
            '由柯西-施瓦茨不等式：$(u+v)^2 \\leq 2(u^2+v^2) = 6$',
            '所以 $u + v \\leq \\sqrt{6} > 2$',
            '因此約束 $u + v \\leq 2$ 是有效的',
            '解得 $x$ 的範圍'
          ],
          answer: '需要進一步計算確定精確範圍'
        },
        hint: '利用換元和柯西-施瓦茨不等式'
      }
    }
  },

  // M10-25: 整數規劃
  {
    id: 'M10_25_01',
    topicId: 'M10_25',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '整數規劃問題：',
    parts: {
      a: {
        question: '求解整數規劃：$\\max z = 3x + 2y$，約束：$x + y \\leq 4.5, 2x + y \\leq 7, x, y \\geq 0$ 且為整數',
        solution: {
          steps: [
            '先求解對應的線性規劃問題（鬆弛問題）',
            '頂點：$(0,0), (0,4.5), (2.5,2), (3.5,0)$',
            '最優解為 $(2.5, 2)$，目標函數值 $11.5$',
            '由於解不是整數，需要尋找整數解',
            '檢查 $(2.5, 2)$ 附近的整數點：$(2,2), (3,2), (2,1), (3,1)$',
            '驗證可行性並計算目標函數值',
            '$(2,2)$: 可行，$z = 10$；$(3,2)$: 不可行；$(3,1)$: 可行，$z = 11$'
          ],
          answer: '最優整數解 $(3,1)$，最大值 $11$'
        },
        hint: '先解鬆弛問題，再在附近尋找最優整數解'
      },
      b: {
        question: '用分支定界法求解上述整數規劃',
        solution: {
          steps: [
            '初始節點：鬆弛問題最優解 $(2.5, 2)$，上界 $11.5$',
            '分支：$x \\leq 2$ 或 $x \\geq 3$',
            '左分支 ($x \\leq 2$)：最優解 $(2, 2.5)$，目標函數值 $11$',
            '右分支 ($x \\geq 3$)：最優解 $(3, 1)$，目標函數值 $11$',
            '左分支需要再分支：$y \\leq 2$ 或 $y \\geq 3$',
            '最終比較所有整數解，選擇最優者'
          ],
          answer: '分支定界法確認最優解 $(3,1)$'
        },
        hint: '系統地分支並比較界限'
      },
      c: {
        question: '分析整數規劃與線性規劃的差異',
        solution: {
          steps: [
            '線性規劃有多項式時間算法（單純形法、內點法）',
            '整數規劃是NP-困難問題，沒有多項式時間算法',
            '整數約束使可行域變為離散點集',
            '最優解可能遠離線性規劃的最優解',
            '求解方法：分支定界、切平面、啟發式算法等'
          ],
          answer: '整數規劃比線性規劃複雜得多'
        },
        hint: '整數約束大大增加了問題的複雜性'
      }
    }
  },

  // M10-26: 臨界點分析
  {
    id: 'M10_26_01',
    topicId: 'M10_26',
    type: 'multi_part',
    difficulty: 'advanced',
    question: '臨界點分析：',
    parts: {
      a: {
        question: '分析函數 $f(x) = x^3 - 3x^2 + 2$ 的臨界點並判斷性質',
        solution: {
          steps: [
            '求一階導數：$f\'(x) = 3x^2 - 6x = 3x(x-2)$',
            '臨界點：$f\'(x) = 0$，得 $x = 0$ 或 $x = 2$',
            '求二階導數：$f\'\'(x) = 6x - 6$',
            '在 $x = 0$：$f\'\'(0) = -6 < 0$，為局部最大值點',
            '在 $x = 2$：$f\'\'(2) = 6 > 0$，為局部最小值點',
            '函數值：$f(0) = 2$，$f(2) = 8 - 12 + 2 = -2$'
          ],
          answer: '局部最大值點 $(0, 2)$，局部最小值點 $(2, -2)$'
        },
        hint: '用二階導數測試判斷臨界點性質'
      },
      b: {
        question: '求約束優化問題的臨界點：$\\max f(x,y) = xy$，約束 $x^2 + y^2 = 1$',
        solution: {
          steps: [
            '使用拉格朗日乘數法：$L = xy - \\lambda(x^2 + y^2 - 1)$',
            '求偏導數：$\\frac{\\partial L}{\\partial x} = y - 2\\lambda x = 0$',
            '$\\frac{\\partial L}{\\partial y} = x - 2\\lambda y = 0$',
            '$\\frac{\\partial L}{\\partial \\lambda} = x^2 + y^2 - 1 = 0$',
            '從前兩個方程：$\\frac{y}{2x} = \\frac{x}{2y}$，得 $y^2 = x^2$，即 $y = \\pm x$',
            '代入約束：$2x^2 = 1$，得 $x = \\pm \\frac{1}{\\sqrt{2}}$',
            '臨界點：$(\\frac{1}{\\sqrt{2}}, \\frac{1}{\\sqrt{2}})$，$(\\frac{1}{\\sqrt{2}}, -\\frac{1}{\\sqrt{2}})$，$(-\\frac{1}{\\sqrt{2}}, \\frac{1}{\\sqrt{2}})$，$(-\\frac{1}{\\sqrt{2}}, -\\frac{1}{\\sqrt{2}})$'
          ],
          answer: '最大值 $\\frac{1}{2}$，最小值 $-\\frac{1}{2}$'
        },
        hint: '拉格朗日乘數法處理等式約束'
      },
      c: {
        question: '分析不等式約束優化中的KKT條件',
        solution: {
          steps: [
            '考慮問題：$\\min f(x)$，約束 $g_i(x) \\leq 0, h_j(x) = 0$',
            'KKT條件：$\\nabla f + \\sum \\mu_i \\nabla g_i + \\sum \\lambda_j \\nabla h_j = 0$',
            '互補鬆弛：$\\mu_i g_i(x) = 0$，$\\mu_i \\geq 0$',
            '約束滿足：$g_i(x) \\leq 0$，$h_j(x) = 0$',
            '這些條件是局部最優解的必要條件'
          ],
          answer: 'KKT條件是約束優化的核心理論'
        },
        hint: 'KKT條件推廣了拉格朗日條件到不等式約束'
      }
    }
  }
];

// Export default object for compatibility
export default {
  topics: M10_TOPICS_21_TO_26,
  problems: M10_PROBLEMS_21_TO_26
}; 